import * as Dialog from '@radix-ui/react-dialog';
import {
  QbLinkState,
  QbSearchResponse,
  type QbSearchResult,
} from '@uit/shared/schemas/quickbooks';
import { type ReactNode, useState } from 'react';
import { useApi } from '../../lib/api.js';
import s from './QuickbooksLinkSection.module.scss';

async function postState(url: string, body?: unknown): Promise<QbLinkState> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  if (!res.ok) {
    throw new Error(res.status === 409 ? 'quickbooks_not_connected' : String(res.status));
  }
  return QbLinkState.parse(await res.json());
}

/**
 * Vendor-page QuickBooks section (T-089): shows the vendor + its services' link
 * state and drives the manual "Link to QuickBooks" + reconcile flow. Admin-only;
 * render this only for admins (the endpoints are admin-scoped).
 */
export function QuickbooksLinkSection({ vendorId }: { vendorId: string }): ReactNode {
  const { data, error } = useApi<QbLinkState>(
    `/api/vendors/${vendorId}/quickbooks/links`,
    QbLinkState,
  );
  const [state, setState] = useState<QbLinkState | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const live = state ?? data;
  if (error || !live) return null;

  const unlinked =
    (live.vendor.qb_vendor_id ? 0 : 1) + live.services.filter((x) => !x.qb_service_id).length;

  async function openAndAutoLink(): Promise<void> {
    setOpen(true);
    setErr(null);
    setBusy(true);
    try {
      setState(await postState(`/api/vendors/${vendorId}/quickbooks/auto-link`));
    } catch (e) {
      setErr(
        e instanceof Error && e.message === 'quickbooks_not_connected'
          ? 'QuickBooks is not connected.'
          : 'Auto-match failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function relink(url: string, body: unknown): Promise<void> {
    setErr(null);
    try {
      setState(await postState(url, body));
    } catch {
      setErr('Link failed.');
    }
  }

  return (
    <section className={s.section}>
      <div className={s.head}>
        <h2 className={s.title}>
          <span className={s.qbmark}>qb</span> QuickBooks
        </h2>
        <span className={live.connected ? s.connOk : s.connOff}>
          {live.connected ? 'connected' : 'not connected'}
        </span>
      </div>
      <p className={s.note}>
        Linking finds existing QuickBooks entities by name — it never creates them. Create the vendor
        &amp; service items in QuickBooks first, then link.
      </p>

      <div className={s.rows}>
        <LinkRow kind="Vendor" name={live.vendor.business_name} linked={!!live.vendor.qb_vendor_id} />
        {live.services.map((svc) => (
          <LinkRow
            key={svc.engagement_id}
            kind="Service"
            name={svc.product_service}
            sub={svc.resource_name}
            linked={!!svc.qb_service_id}
          />
        ))}
      </div>

      {!live.connected ? (
        <p className={s.muted}>Connect QuickBooks in Settings to link this vendor.</p>
      ) : unlinked === 0 ? (
        <p className={s.allLinked}>✓ Fully linked to QuickBooks</p>
      ) : (
        <button type="button" className={s.linkBtn} onClick={() => void openAndAutoLink()}>
          Link to QuickBooks
        </button>
      )}

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className={s.overlay} />
          <Dialog.Content className={s.modal}>
            <Dialog.Title className={s.modalTitle}>
              Link “{live.vendor.business_name}” to QuickBooks
            </Dialog.Title>
            <p className={s.modalSub}>
              Matched by name. Vendor-scoped: the vendor + every unlinked service. Never creates
              entities in QuickBooks.
            </p>
            {err && (
              <div className={s.formError} role="alert">
                {err}
              </div>
            )}
            {busy && <p className={s.muted}>Matching…</p>}
            {!busy && state && (
              <div className={s.modalBody}>
                <ModalRow
                  kind="Vendor"
                  name={state.vendor.business_name}
                  linkedId={state.vendor.qb_vendor_id}
                  searchType="vendor"
                  onPick={(qbId) =>
                    relink(`/api/vendors/${vendorId}/quickbooks/link-vendor`, { qb_vendor_id: qbId })
                  }
                />
                {state.services.map((svc) => (
                  <ModalRow
                    key={svc.engagement_id}
                    kind="Service"
                    name={svc.product_service}
                    sub={svc.resource_name}
                    linkedId={svc.qb_service_id}
                    searchType="service"
                    onPick={(qbId) =>
                      relink(`/api/engagements/${svc.engagement_id}/quickbooks/link-service`, {
                        qb_service_id: qbId,
                      })
                    }
                  />
                ))}
              </div>
            )}
            <div className={s.modalFoot}>
              <Dialog.Close asChild>
                <button type="button" className={s.doneBtn}>
                  Done
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </section>
  );
}

function LinkRow({
  kind,
  name,
  sub,
  linked,
}: {
  kind: string;
  name: string;
  sub?: string;
  linked: boolean;
}): ReactNode {
  return (
    <div className={s.row}>
      <span className={s.rowKind}>{kind}</span>
      <span className={s.rowName}>
        {name}
        {sub && <small> · {sub}</small>}
      </span>
      <span className={linked ? s.linked : s.unlinked}>{linked ? '✓ Linked' : '○ Not linked'}</span>
    </div>
  );
}

function ModalRow({
  kind,
  name,
  sub,
  linkedId,
  searchType,
  onPick,
}: {
  kind: string;
  name: string;
  sub?: string;
  linkedId: string | null;
  searchType: 'vendor' | 'service';
  onPick: (qbId: string) => Promise<void>;
}): ReactNode {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<QbSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);

  async function search(): Promise<void> {
    setSearching(true);
    try {
      const res = await fetch(
        `/api/quickbooks/search?type=${searchType}&q=${encodeURIComponent(q.trim())}`,
        { credentials: 'include' },
      );
      setResults(res.ok ? QbSearchResponse.parse(await res.json()).results : []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className={s.item}>
      <div className={s.itemTop}>
        <span className={s.itemKind}>{kind}</span>
        <span className={s.itemName}>
          {name}
          {sub && <small>{sub}</small>}
        </span>
        <span className={linkedId ? s.statusOk : s.statusWarn}>
          {linkedId ? '✓ Linked' : '○ No match'}
        </span>
      </div>
      {!linkedId && (
        <div className={s.reconcile}>
          <div className={s.searchRow}>
            <input
              className={s.searchInput}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search QuickBooks ${searchType}s…`}
            />
            <button
              type="button"
              className={s.searchBtn}
              onClick={() => void search()}
              disabled={!q.trim() || searching}
            >
              {searching ? '…' : 'Search'}
            </button>
          </div>
          {results && results.length === 0 && (
            <p className={s.empty}>
              No matches in QuickBooks. Create it there first, then search again.
            </p>
          )}
          {results?.map((r) => (
            <div key={r.id} className={s.candidate}>
              <span className={s.candName}>
                {r.name} <span className={s.candId}>#{r.id}</span>
              </span>
              <button type="button" className={s.candBtn} onClick={() => void onPick(r.id)}>
                Link
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
